import { useMemo } from 'react';
import { safeStringify } from '../utils/safeStringify';

function readStructuredHttpRequest(output: unknown): unknown {
  let parsedOutput = output;

  if (typeof output === 'string') {
    try {
      parsedOutput = JSON.parse(output);
    } catch {
      return undefined;
    }
  }

  if (typeof parsedOutput !== 'object' || parsedOutput === null) {
    return undefined;
  }

  return Reflect.get(parsedOutput, 'request');
}

type HttpRequestData = {
  method: string;
  url: string;
  headers?: Record<string, string | string[]>;
  body?: string;
  bodyEncoding?: 'utf8' | 'base64';
};

type HttpResponse = {
  status?: number;
  statusMessage?: string;
  headers?: Record<string, string | string[]>;
  body?: string;
  httpVersion?: string;
};

export function useHttpToolData({
  input,
  output,
}: {
  input: unknown;
  output?: unknown;
}) {
  const httpResponseData = useMemo(() => {
    const extractHttpResponseData = (output: unknown): HttpResponse | null => {
      // Parse JSON string if needed
      let parsedOutput = output;

      if (typeof output === 'string') {
        try {
          parsedOutput = JSON.parse(output);
        } catch {
          // If parsing fails, treat as plain string (not HTTP response)
          return null;
        }
      }

      if (typeof parsedOutput !== 'object' || parsedOutput === null) {
        return null;
      }

      const obj = parsedOutput as Record<string, unknown>;

      // Extract status code
      const status =
        typeof obj.statusCode === 'number'
          ? obj.statusCode
          : typeof obj.status === 'number'
            ? obj.status
            : undefined;

      // Extract status message
      const statusMessage =
        typeof obj.statusText === 'string'
          ? obj.statusText
          : typeof obj.statusMessage === 'string'
            ? obj.statusMessage
            : undefined;

      // Extract headers
      const headers =
        obj.headers && typeof obj.headers === 'object'
          ? (obj.headers as Record<string, string | string[]>)
          : undefined;

      // Extract body - use safe stringify for large objects
      let body: string | undefined;

      if (obj.body) {
        body =
          typeof obj.body === 'string' ? obj.body : safeStringify(obj.body);
      } else if (obj.data) {
        body =
          typeof obj.data === 'string' ? obj.data : safeStringify(obj.data);
      } else if (obj.content) {
        body =
          typeof obj.content === 'string'
            ? obj.content
            : safeStringify(obj.content);
      }

      // Extract HTTP version
      const httpVersion =
        typeof obj.httpVersion === 'string' ? obj.httpVersion : undefined;

      // Handle nested format: { request: {...}, response: { statusCode, statusText, result, headers } }
      // produced by buildHttpStructuredOutput (used by http-tool and http-executor).
      if (
        status === undefined &&
        !body &&
        obj.response &&
        typeof obj.response === 'object'
      ) {
        const nested = obj.response as Record<string, unknown>;
        const nestedStatus =
          typeof nested.statusCode === 'number'
            ? nested.statusCode
            : typeof nested.status === 'number'
              ? nested.status
              : undefined;
        const nestedBody =
          typeof nested.result === 'string'
            ? nested.result
            : typeof nested.body === 'string'
              ? nested.body
              : typeof nested.data === 'string'
                ? nested.data
                : undefined;
        const nestedStatusMessage =
          typeof nested.statusText === 'string'
            ? nested.statusText
            : typeof nested.statusMessage === 'string'
              ? nested.statusMessage
              : undefined;
        const nestedHeaders =
          nested.headers && typeof nested.headers === 'object'
            ? (nested.headers as Record<string, string | string[]>)
            : undefined;
        const nestedError =
          typeof nested.error === 'string' && nested.error.trim().length > 0
            ? nested.error
            : undefined;

        if (nestedStatus !== undefined || nestedBody || nestedError) {
          return {
            status: nestedStatus,
            statusMessage: nestedStatusMessage,
            headers: nestedHeaders,
            body: nestedBody ?? nestedError,
            httpVersion:
              typeof nested.httpVersion === 'string'
                ? nested.httpVersion
                : undefined,
          };
        }
      }

      if (status === undefined && !body) {
        return null;
      }

      return {
        status,
        statusMessage,
        headers,
        body,
        httpVersion,
      };
    };

    return extractHttpResponseData(output);
  }, [output]);

  const httpRequestData = useMemo(() => {
    const extractHttpRequestData = (input: unknown): HttpRequestData | null => {
      if (typeof input !== 'object' || input === null) {
        return null;
      }

      const obj = input as Record<string, unknown>;

      // Check if we have the minimum required fields
      if (typeof obj.url !== 'string') {
        return null;
      }

      // Extract headers and body from options if they exist
      const options =
        obj.options && typeof obj.options === 'object'
          ? (obj.options as Record<string, unknown>)
          : undefined;

      const headers =
        options?.headers && typeof options.headers === 'object'
          ? (options.headers as Record<string, string>)
          : obj.headers && typeof obj.headers === 'object'
            ? (obj.headers as Record<string, string>)
            : undefined;

      let finalUrl = obj.url;

      const rawQueryParams =
        (options &&
          (options.queryParams || options.params || options.searchParams)) ||
        obj.queryParams ||
        obj.params ||
        obj.searchParams;

      const isQueryParamsObject =
        rawQueryParams &&
        typeof rawQueryParams === 'object' &&
        !Array.isArray(rawQueryParams);

      if (finalUrl && isQueryParamsObject) {
        try {
          // If URL is relative, we need to handle it differently
          const isAbsoluteUrl =
            finalUrl.startsWith('http://') || finalUrl.startsWith('https://');

          if (isAbsoluteUrl) {
            const url = new URL(finalUrl);

            Object.entries(rawQueryParams as Record<string, unknown>).forEach(
              ([key, value]) => {
                url.searchParams.append(key, String(value));
              },
            );
            finalUrl = url.toString();
          } else {
            const params = new URLSearchParams();

            Object.entries(rawQueryParams as Record<string, unknown>).forEach(
              ([key, value]) => {
                params.append(key, String(value));
              },
            );
            const queryString = params.toString();

            if (queryString) {
              finalUrl = finalUrl.includes('?')
                ? `${finalUrl}&${queryString}`
                : `${finalUrl}?${queryString}`;
            }
          }
        } catch {
          // Keep original URL if parsing fails
        }
      }

      // If the URL is relative (doesn't start with http:// or https://),
      // try to reconstruct it from the Host header
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
        if (headers) {
          // Look for Host header (case-insensitive)
          const hostEntry = Object.entries(headers).find(
            ([key]) => key.toLowerCase() === 'host',
          );

          if (hostEntry) {
            const hostHeader = hostEntry[1];
            // Determine protocol (assume https if not specified)
            const protocol = 'https://';

            finalUrl = `${protocol}${hostHeader}${finalUrl}`;
          }
        }
      }

      // Helper to convert object to URL-encoded string
      const toUrlEncoded = (data: Record<string, unknown>): string => {
        return Object.entries(data)
          .map(
            ([key, value]) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(String(value ?? ''))}`,
          )
          .join('&');
      };

      // Helper to check if content-type is form-urlencoded
      const isFormUrlEncoded = (): boolean => {
        if (!headers) return false;
        const contentType = Object.entries(headers).find(
          ([key]) => key.toLowerCase() === 'content-type',
        );

        return (
          contentType?.[1]
            ?.toString()
            .toLowerCase()
            .includes('x-www-form-urlencoded') ?? false
        );
      };

      // Handle body - use safe stringify for large objects, but handle form-urlencoded properly
      let body: string | undefined;
      const rawBody = options?.body ?? obj.body ?? options?.data ?? obj.data;
      const rawFormData = options?.formData ?? obj.formData;

      if (rawBody) {
        if (typeof rawBody === 'string') {
          body = rawBody;
        } else if (typeof rawBody === 'object' && rawBody !== null) {
          // For form-urlencoded, convert object to URL-encoded string
          if (isFormUrlEncoded()) {
            body = toUrlEncoded(rawBody as Record<string, unknown>);
          } else {
            body = safeStringify(rawBody);
          }
        }
      } else if (rawFormData) {
        if (typeof rawFormData === 'string') {
          body = rawFormData;
        } else if (typeof rawFormData === 'object' && rawFormData !== null) {
          // Form data is typically URL-encoded
          body = toUrlEncoded(rawFormData as Record<string, unknown>);
        }
      }

      // Extract method - check options first, then top level
      const method =
        typeof options?.method === 'string'
          ? options.method
          : typeof obj.method === 'string'
            ? obj.method
            : 'GET';

      return {
        method,
        url: finalUrl,
        headers,
        body,
      };
    };

    const fromInput = extractHttpRequestData(input);

    if (fromInput) {
      return fromInput;
    }

    return extractHttpRequestData(readStructuredHttpRequest(output));
  }, [input, output]);

  return {
    httpRequestData,
    httpResponseData,
  };
}
